import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button, Modal, Select, TextField } from "@xfrontend/xui-react-plus";
import { Delete } from "@xfrontend/xicon";
import type { CustomPromptTemplate, PromptTemplate } from "./promptTemplates";
import { PROMPT_TEMPLATE_GROUPS } from "./promptTemplates";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type FormMessage = { id: number; role: "user" | "assistant"; content: string };

export type TemplateFormValues = Omit<CustomPromptTemplate, "id" | "createdAt">;

export type TemplateFormModalProps = {
  show: boolean;
  onClose: () => void;
  onSave: (values: TemplateFormValues) => void;
  /** Pre-fill fields (for edit / save-current). */
  initialValues?: Partial<CustomPromptTemplate> & { category?: string };
  title?: string;
  /** Extra categories from user-custom templates already in localStorage. */
  extraCategories?: string[];
};

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const CUSTOM_VALUE = "__custom__";

const builtInCategories = PROMPT_TEMPLATE_GROUPS.map((g) => g.category);

type CategoryOption = { value: string; label: string };

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const TemplateFormModal: React.FC<TemplateFormModalProps> = ({
  show,
  onClose,
  onSave,
  initialValues,
  title,
  extraCategories = [],
}) => {
  const { t } = useTranslation("sandbox");
  const resolvedTitle = title ?? t("templateForm.newTitle");
  const roleOptions = [
    { value: "user", label: t("templateForm.roleUser") },
    { value: "assistant", label: t("templateForm.roleAssistant") },
  ];
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [customCategoryInput, setCustomCategoryInput] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [messages, setMessages] = useState<FormMessage[]>([
    { id: 1, role: "user", content: "" },
  ]);

  // Build category options
  const allCategories = [...new Set([...builtInCategories, ...extraCategories])];
  const categoryOptions: CategoryOption[] = [
    ...allCategories.map((c) => ({ value: c, label: c })),
    { value: CUSTOM_VALUE, label: t("templateForm.categoryCustom") },
  ];

  // Reset form when modal opens
  useEffect(() => {
    if (!show) return;
    setName(initialValues?.name ?? "");
    setDescription(initialValues?.description ?? "");
    setSystemPrompt(initialValues?.systemPrompt ?? "");

    const msgs: FormMessage[] =
      initialValues?.messages && initialValues.messages.length > 0
        ? initialValues.messages.map((m, i) => ({
            id: i + 1,
            role: m.role as "user" | "assistant",
            content: m.content,
          }))
        : [{ id: 1, role: "user", content: "" }];
    setMessages(msgs);

    const cat = initialValues?.category ?? "";
    if (cat && !allCategories.includes(cat)) {
      setIsCustomCategory(true);
      setCustomCategoryInput(cat);
      setCategory(CUSTOM_VALUE);
    } else {
      setIsCustomCategory(false);
      setCustomCategoryInput("");
      setCategory(cat || allCategories[0] || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleCategoryChange = (selected: unknown) => {
    const opt = selected as CategoryOption | null;
    if (!opt) return;
    if (opt.value === CUSTOM_VALUE) {
      setIsCustomCategory(true);
      setCategory(CUSTOM_VALUE);
    } else {
      setIsCustomCategory(false);
      setCustomCategoryInput("");
      setCategory(opt.value);
    }
  };

  const addMessage = () => {
    setMessages((prev) => [...prev, { id: Date.now(), role: "user", content: "" }]);
  };

  const removeMessage = (id: number) => {
    setMessages((prev) => (prev.length <= 1 ? prev : prev.filter((m) => m.id !== id)));
  };

  const updateMessage = (id: number, field: "role" | "content", value: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)),
    );
  };

  const resolvedCategory = isCustomCategory ? customCategoryInput.trim() : category;

  const isValid =
    name.trim() &&
    description.trim() &&
    resolvedCategory &&
    messages.some((m) => m.content.trim());

  const handleSave = () => {
    if (!isValid) return;
    onSave({
      name: name.trim(),
      description: description.trim(),
      category: resolvedCategory,
      systemPrompt: systemPrompt.trim(),
      messages: messages
        .filter((m) => m.content.trim())
        .map((m) => ({ role: m.role, content: m.content.trim() })),
    });
    onClose();
  };

  return (
    <StyledModal
      title={resolvedTitle}
      show={show}
      onClose={onClose}
      showCloseBtn
      restoreFocus={false}
    >
      <Modal.Body>
        <FormGroup>
          <Label>{t("templateForm.name")}</Label>
          <TextField
            name="template-name"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            placeholder={t("templateForm.namePlaceholder")}
          />
        </FormGroup>

        <FormGroup>
          <Label>{t("templateForm.description")}</Label>
          <TextField
            name="template-description"
            value={description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            placeholder={t("templateForm.descriptionPlaceholder")}
          />
        </FormGroup>

        <FormGroup>
          <Label>{t("templateForm.category")}</Label>
          <Select
            isSearchable={false}
            options={categoryOptions}
            value={categoryOptions.find((o) => o.value === (isCustomCategory ? CUSTOM_VALUE : category))}
            onChange={handleCategoryChange}
          />
          {isCustomCategory && (
            <TextField
              name="custom-category"
              value={customCategoryInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomCategoryInput(e.target.value)}
              placeholder={t("templateForm.categoryCustomPlaceholder")}
              style={{ marginTop: 8 }}
            />
          )}
        </FormGroup>

        <FormGroup>
          <Label>{t("templateForm.systemPrompt")}</Label>
          <TextField
            as="textarea"
            name="template-system-prompt"
            value={systemPrompt}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSystemPrompt(e.target.value)}
            placeholder={t("templateForm.systemPromptPlaceholder")}
            rows={3}
          />
        </FormGroup>

        <FormGroup>
          <Label>{t("templateForm.messages")}</Label>
          {messages.map((msg) => (
            <MessageRow key={msg.id}>
              <div style={{ width: 200 }}>
                <Select
                  isSearchable={false}
                  options={roleOptions}
                  value={roleOptions.find((o) => o.value === msg.role)}
                  onChange={(selected: unknown) => {
                    const opt = selected as { value: string } | null;
                    if (opt) updateMessage(msg.id, "role", opt.value);
                  }}
                />
              </div>
              <TextField
                as="textarea"
                name={`msg-${msg.id}`}
                value={msg.content}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  updateMessage(msg.id, "content", e.target.value)
                }
                placeholder={t("templateForm.messageContentPlaceholder")}
                rows={2}
                style={{ flex: 1 }}
              />
              {messages.length > 1 && (
                <DeleteBtn type="button" onClick={() => removeMessage(msg.id)}>
                  <Delete size={14} color="var(--x-gray-400, #cfd0d3)" />
                </DeleteBtn>
              )}
            </MessageRow>
          ))}
          <AddMsgBtn type="button" onClick={addMessage}>
            {t("templateForm.addMessage")}
          </AddMsgBtn>
        </FormGroup>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t("templateForm.cancel")}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!isValid}>
          {t("templateForm.save")}
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default TemplateFormModal;

/* ------------------------------------------------------------------ */
/*  Styled                                                             */
/* ------------------------------------------------------------------ */

const StyledModal = styled(Modal)`
  .modal-dialog {
    width: 520px;
    max-width: calc(100% - 32px);
  }

  .modal-content {
    background: var(--x-bg-500, #2d293a);
    border: 1px solid var(--x-purple-500, #494358);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: var(--x-gray-200, #f6f6f7);
  margin-bottom: 6px;
`;

const MessageRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 4px;

  &:hover {
    opacity: 0.7;
  }
`;

const AddMsgBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 4px;
  border: none;
  background: var(--x-theme-secondary, #73698a);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 13px;
  cursor: pointer;
  width: fit-content;

  &:hover {
    opacity: 0.85;
  }
`;
